import { useState } from "react";
import { 
  X, ArrowRight, ArrowLeft, CheckCircle, Users, Briefcase, 
  Award, BookOpen, Target, ClipboardList, Info
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const RoleSkillsIdentifier = ({ onClose }) => {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [formData, setFormData] = useState({
    standard: "",
    organization_size: "",
    selected_roles: [],
    current_training_level: ""
  });

  const standards = [
    { id: "iso9001", name: "ISO 9001 - Quality Management", icon: "🏆" },
    { id: "iso22000", name: "ISO 22000 - Food Safety", icon: "🍽️" },
    { id: "fssc22000", name: "FSSC 22000 - Food Safety Certification", icon: "✅" },
    { id: "iso27001", name: "ISO 27001 - Information Security", icon: "🔒" },
    { id: "iso45001", name: "ISO 45001 - Health & Safety", icon: "⛑️" },
    { id: "iso14001", name: "ISO 14001 - Environmental", icon: "🌿" }
  ];

  const organizationSizes = [
    { id: "small", name: "Small (1-50)", desc: "Typically 2-3 key roles" },
    { id: "medium", name: "Medium (51-250)", desc: "5-8 specialized roles" },
    { id: "large", name: "Large (251+)", desc: "Full role structure" }
  ];

  const isoRoles = [
    { 
      id: "top_management", 
      name: "Top Management", 
      desc: "CEO, Directors, Owners",
      responsibilities: ["Leadership commitment", "Policy approval", "Resource allocation", "Management review"],
      requiredTraining: ["Executive Briefing", "Management System Overview"]
    },
    { 
      id: "management_rep", 
      name: "Management Representative", 
      desc: "QMS/FSMS Manager",
      responsibilities: ["System ownership", "Performance reporting", "Continual improvement", "External liaison"],
      requiredTraining: ["Lead Implementer", "Internal Auditor", "Risk Management"]
    },
    { 
      id: "internal_auditor", 
      name: "Internal Auditor", 
      desc: "Audit team members",
      responsibilities: ["Audit planning", "Conducting audits", "Reporting findings", "Follow-up verification"],
      requiredTraining: ["Internal Auditor Training", "Audit Techniques"]
    },
    { 
      id: "process_owner", 
      name: "Process Owner", 
      desc: "Department Managers, Supervisors",
      responsibilities: ["Process management", "KPI monitoring", "Corrective actions", "Training coordination"],
      requiredTraining: ["Foundation Training", "Process Management", "CAPA"]
    },
    { 
      id: "document_controller", 
      name: "Document Controller", 
      desc: "Records & Documentation",
      responsibilities: ["Document control", "Records management", "Version control", "Distribution"],
      requiredTraining: ["Document Control Training", "Records Management"]
    },
    { 
      id: "haccp_team_leader", 
      name: "HACCP Team Leader", 
      desc: "Food Safety Team (ISO 22000/FSSC)",
      responsibilities: ["Hazard analysis", "CCP management", "HACCP plan maintenance", "Verification"],
      requiredTraining: ["HACCP Advanced", "Lead Implementer", "Food Safety Culture"],
      standards: ["iso22000", "fssc22000"]
    },
    { 
      id: "food_safety_team", 
      name: "Food Safety Team Member", 
      desc: "Cross-functional team (ISO 22000/FSSC)",
      responsibilities: ["Hazard identification", "CCP monitoring", "Prerequisite programs", "Traceability"],
      requiredTraining: ["HACCP Foundation", "GMP/GHP", "Allergen Management"],
      standards: ["iso22000", "fssc22000"]
    },
    { 
      id: "it_security_officer", 
      name: "Information Security Officer", 
      desc: "ISMS Manager (ISO 27001)",
      responsibilities: ["Risk assessment", "Security controls", "Incident management", "Awareness programs"],
      requiredTraining: ["ISO 27001 Lead Implementer", "Risk Assessment", "Incident Response"],
      standards: ["iso27001"]
    },
    { 
      id: "safety_officer", 
      name: "Health & Safety Officer", 
      desc: "OH&S Manager (ISO 45001)",
      responsibilities: ["Hazard identification", "Risk controls", "Incident investigation", "Emergency preparedness"],
      requiredTraining: ["ISO 45001 Lead Implementer", "Risk Assessment", "Incident Investigation"],
      standards: ["iso45001"]
    },
    { 
      id: "environmental_officer", 
      name: "Environmental Officer", 
      desc: "EMS Manager (ISO 14001)",
      responsibilities: ["Aspect identification", "Compliance obligations", "Environmental monitoring", "Waste management"],
      requiredTraining: ["ISO 14001 Lead Implementer", "Environmental Aspects", "Legal Compliance"],
      standards: ["iso14001"]
    },
    { 
      id: "operator", 
      name: "Operator / Line Staff", 
      desc: "Production, Operations",
      responsibilities: ["Procedure compliance", "Record keeping", "Nonconformance reporting", "Housekeeping"],
      requiredTraining: ["Awareness Training", "Job-specific Procedures", "GMP/GHP"]
    }
  ];

  const trainingLevels = [
    { id: "none", name: "No formal training", desc: "Staff have not received ISO/management system training" },
    { id: "basic", name: "Basic awareness", desc: "Some staff have received introductory training" },
    { id: "intermediate", name: "Intermediate", desc: "Key staff are trained, some gaps exist" },
    { id: "advanced", name: "Well-trained", desc: "Most roles have appropriate training" }
  ];

  const getFilteredRoles = () => {
    return isoRoles.filter(role => {
      if (!role.standards) return true;
      return role.standards.includes(formData.standard);
    });
  };

  const toggleRole = (roleId) => {
    setFormData(prev => ({
      ...prev,
      selected_roles: prev.selected_roles.includes(roleId)
        ? prev.selected_roles.filter(r => r !== roleId)
        : [...prev.selected_roles, roleId]
    }));
  };

  const analyzeRoles = () => {
    setIsLoading(true);
    
    setTimeout(async () => {
      const selectedRoleDetails = isoRoles.filter(r => formData.selected_roles.includes(r.id));
      
      const roleAnalysis = selectedRoleDetails.map(role => {
        const trainingGap = formData.current_training_level === 'none' ? 'high' :
                          formData.current_training_level === 'basic' ? 'medium' : 'low';
        return {
          role: role.name,
          description: role.desc,
          key_responsibilities: role.responsibilities,
          required_training: role.requiredTraining,
          training_gap: trainingGap,
          priority: role.id === 'management_rep' || role.id === 'top_management' ? 'critical' :
                   role.id === 'internal_auditor' || role.id === 'haccp_team_leader' ? 'high' : 'medium'
        };
      });

      // Compile all unique training needs
      const allTraining = new Set();
      selectedRoleDetails.forEach(role => {
        role.requiredTraining.forEach(t => allTraining.add(t));
      });

      const trainingPlan = Array.from(allTraining).map(training => ({
        course: training,
        applicable_roles: selectedRoleDetails.filter(r => r.requiredTraining.includes(training)).map(r => r.name),
        priority: training.includes('Lead') || training.includes('Executive') ? 'high' : 'medium',
        delivery: training.includes('Awareness') || training.includes('Foundation') ? 'In-house possible' : 'External recommended'
      }));

      const analysisResult = {
        id: `roles-${Date.now()}`,
        standard: formData.standard,
        organization_size: formData.organization_size,
        current_training_level: formData.current_training_level,
        total_roles_analyzed: roleAnalysis.length,
        critical_roles: roleAnalysis.filter(r => r.priority === 'critical').length,
        high_priority_roles: roleAnalysis.filter(r => r.priority === 'high').length,
        role_analysis: roleAnalysis,
        training_plan: trainingPlan.sort((a, b) => a.priority === 'high' ? -1 : 1),
        recommendations: [
          "Prioritize training for Management Representative and Top Management first",
          "Train internal auditors before conducting first internal audit cycle",
          "Consider in-house awareness training for operators to reduce costs",
          "Plan for refresher training annually to maintain competency"
        ],
        next_steps: [
          "Review role assignments and ensure all positions are filled",
          "Develop a training matrix with target completion dates",
          "Budget for external training programs (Lead Implementer, Lead Auditor)",
          "Contact our team for customized in-house training programs"
        ]
      };

      // Save to session
      const sessionId = localStorage.getItem("chatbot_session_id");
      if (sessionId) {
        try {
          await axios.post(`${API}/tools/session/save`, {
            session_id: sessionId,
            tool_name: "role_skills_identifier",
            result: analysisResult
          });
        } catch (e) {
          console.error("Failed to save session:", e);
        }
      }

      setResult(analysisResult);
      setIsLoading(false);
    }, 1500);
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case "critical": return "bg-red-100 text-red-700";
      case "high": return "bg-amber-100 text-amber-700";
      case "medium": return "bg-blue-100 text-blue-700";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Which standard are you implementing?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                This determines the specific roles and competencies required.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {standards.map((std) => (
                <button
                  key={std.id}
                  onClick={() => { setFormData({ ...formData, standard: std.id }); setStep(2); }}
                  className={`p-4 rounded-xl border-2 text-left transition-all hover:border-brand ${
                    formData.standard === std.id ? 'border-brand bg-brand/5' : 'border-slate-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{std.icon}</span>
                    <span className="font-medium">{std.name}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">What&apos;s your organization size?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                This affects the number of roles typically needed.
              </p>
            </div>
            <div className="space-y-3">
              {organizationSizes.map((size) => (
                <button
                  key={size.id}
                  onClick={() => setFormData({ ...formData, organization_size: size.id })}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    formData.organization_size === size.id 
                      ? 'border-brand bg-brand/5' 
                      : 'border-slate-200 hover:border-brand/50'
                  }`}
                >
                  <div className="font-medium">{size.name}</div>
                  <div className="text-sm text-muted-foreground">{size.desc}</div>
                </button>
              ))}
            </div>
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button 
                onClick={() => setStep(3)} 
                className="bg-brand hover:bg-brand-dark"
                disabled={!formData.organization_size}
              >
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Select roles to analyze</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Choose the roles that exist or will exist in your organization.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-80 overflow-y-auto">
              {getFilteredRoles().map((role) => (
                <button
                  key={role.id}
                  onClick={() => toggleRole(role.id)}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    formData.selected_roles.includes(role.id) 
                      ? 'border-brand bg-brand/5' 
                      : 'border-slate-200 hover:border-brand/50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      formData.selected_roles.includes(role.id) 
                        ? 'bg-brand border-brand' 
                        : 'border-slate-300'
                    }`}>
                      {formData.selected_roles.includes(role.id) && (
                        <CheckCircle className="h-3 w-3 text-white" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-sm">{role.name}</div>
                      <div className="text-xs text-muted-foreground">{role.desc}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button 
                onClick={() => setStep(4)} 
                className="bg-brand hover:bg-brand-dark"
                disabled={formData.selected_roles.length === 0}
              >
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Current training level</h3>
              <p className="text-sm text-muted-foreground mb-4">
                What&apos;s the current training status for these roles?
              </p>
            </div>
            <div className="space-y-3">
              {trainingLevels.map((level) => (
                <button
                  key={level.id}
                  onClick={() => setFormData({ ...formData, current_training_level: level.id })}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    formData.current_training_level === level.id 
                      ? 'border-brand bg-brand/5' 
                      : 'border-slate-200 hover:border-brand/50'
                  }`}
                >
                  <div className="font-medium">{level.name}</div>
                  <div className="text-sm text-muted-foreground">{level.desc}</div>
                </button>
              ))}
            </div>
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(3)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button 
                onClick={analyzeRoles} 
                className="bg-brand hover:bg-brand-dark"
                disabled={isLoading || !formData.current_training_level}
              >
                {isLoading ? "Analyzing..." : "Analyze Roles & Skills"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (result) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-primary">Role & Skills Analysis</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Summary */}
        <div className="bg-brand/5 border border-brand/20 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <Users className="h-5 w-5 text-brand" />
            <span className="font-semibold">Analysis Summary</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div className="text-center">
              <p className="text-2xl font-bold text-brand">{result.total_roles_analyzed}</p>
              <p className="text-xs text-muted-foreground">Roles Analyzed</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">{result.critical_roles}</p>
              <p className="text-xs text-muted-foreground">Critical Priority</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-600">{result.high_priority_roles}</p>
              <p className="text-xs text-muted-foreground">High Priority</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold">{result.training_plan.length}</p>
              <p className="text-xs text-muted-foreground">Training Courses</p>
            </div>
          </div>
        </div>

        {/* Role Analysis */}
        <div className="mb-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-brand" />
            Role Responsibilities & Training Needs
          </h3>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {result.role_analysis.map((role, index) => (
              <div key={index} className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <span className="font-medium">{role.role}</span>
                    <p className="text-xs text-muted-foreground">{role.description}</p>
                  </div>
                  <Badge className={getPriorityBadge(role.priority)}>{role.priority}</Badge>
                </div>
                <div className="mt-2">
                  <p className="text-xs font-medium text-slate-600 mb-1">Key Responsibilities:</p>
                  <div className="flex flex-wrap gap-1">
                    {role.key_responsibilities.slice(0, 3).map((resp, i) => (
                      <span key={i} className="text-xs bg-white px-2 py-0.5 rounded">{resp}</span>
                    ))}
                  </div>
                </div>
                <div className="mt-2">
                  <p className="text-xs font-medium text-slate-600 mb-1">Required Training:</p>
                  <div className="flex flex-wrap gap-1">
                    {role.required_training.map((train, i) => (
                      <span key={i} className="text-xs bg-brand/10 text-brand px-2 py-0.5 rounded">{train}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Training Plan */}
        <div className="bg-slate-50 rounded-xl p-4 mb-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-brand" />
            Consolidated Training Plan
          </h3>
          <div className="space-y-2">
            {result.training_plan.slice(0, 6).map((item, index) => (
              <div key={index} className="bg-white rounded-lg p-3 flex items-center justify-between">
                <div>
                  <span className="font-medium text-sm">{item.course}</span>
                  <p className="text-xs text-muted-foreground">
                    For: {item.applicable_roles.slice(0, 2).join(", ")}
                    {item.applicable_roles.length > 2 && ` +${item.applicable_roles.length - 2} more`}
                  </p>
                </div>
                <div className="text-right">
                  <Badge className={item.priority === 'high' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}>
                    {item.priority}
                  </Badge>
                  <p className="text-xs text-muted-foreground mt-1">{item.delivery}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-slate-50 rounded-xl p-4 mb-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Target className="h-4 w-4 text-brand" />
            Next Steps
          </h3>
          <ul className="space-y-2">
            {result.next_steps.map((step, i) => (
              <li key={i} className="text-sm flex items-start gap-2">
                <span className="font-bold text-brand">{i + 1}.</span> {step}
              </li>
            ))}
          </ul>
        </div>

        {/* Disclaimer */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
          <p className="text-xs text-amber-700 flex items-start gap-2">
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
            This analysis provides guidance on typical role requirements. Actual competency needs should be 
            verified against the specific standard requirements and your organization&apos;s context.
          </p>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={() => { setResult(null); setStep(1); }} className="flex-1">
            Start Over
          </Button>
          <Button onClick={onClose} className="flex-1 bg-brand hover:bg-brand-dark">
            Request Training Consultation
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-primary">ISO Role-Based Skills Identifier</h2>
          <p className="text-sm text-muted-foreground">Step {step} of 4</p>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-slate-100 rounded-full h-2 mb-8">
        <div 
          className="bg-brand h-2 rounded-full transition-all duration-300"
          style={{ width: `${(step / 4) * 100}%` }}
        />
      </div>

      {renderStep()}
    </div>
  );
};

export default RoleSkillsIdentifier;
